import _to_consumable_array from "@swc/helpers/src/_to_consumable_array.mjs";
import { ProductType, SourceType } from "@core/api";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { createSelector } from "reselect";
import { LicenseConstants } from "../constants/LicenseConstants.js";
import { LicenseHelper } from "../helpers/LicenseHelper.js";
import { LicenseStore } from "../store/LicenseStore.js";
dayjs.extend(isSameOrBefore);
export var permittedUndefinedValues = [
    "listFieldValuesQuotaPerOrgLimit"
];
export var editableSearchOrder = [
    "maxPipelinesLimit",
    "maxPipelineConditionsLimit",
    "maxPipelineStatementsLimit",
    "queryQuotaPerOrgLimit",
    "querySuggestQuotaPerOrgLimit",
    "listFieldValuesQuotaPerOrgLimit"
];
export var editableContentOrder = [
    "numberOfSourcesLimit",
    "numberOfMappingPerSourcesLimit",
    "numberOfSourceTypesLimit",
    "numberOfFieldsLimit",
    "licenseExtensionsLimit",
    "numberOfExtensionsPerSourceLimit",
    "numberOfSecurityProvidersLimit",
    "PushApiFileSizeLimit",
    "numberOfDocumentsLimit",
    "pushApiMaximumDailyInvocations",
    "pushApiMaximumDailyDocumentLimit",
    "pushApiMaximumDailyRelationshipLimit"
];
export var contentHistoryLimits = [
    "numberOfDocumentsLimit",
    "pushApiMaximumDailyInvocations",
    "pushApiMaximumDailyDocumentLimit",
    "pushApiMaximumDailyRelationshipLimit"
];
export var throttlingLimits = [
    "listFieldValuesQuotaPerOrgLimit",
    "numberOfDailyEmailNotificationsSentPerOrganizationLimit",
    "pushApiMaximumDailyDocumentLimit",
    "pushApiMaximumDailyInvocations",
    "pushApiMaximumDailyRelationshipLimit",
    "queryQuotaPerOrgLimit",
    "querySuggestQuotaPerOrgLimit",
    "passageRetrievalQuotaPerOrgLimit",
    "relevanceGenerativeAnsweringQueryPerSecondLimit",
    "streamApiMaximumBurstInvocation",
    "pushApiBurstRelationshipLimit",
    "pushApiBurstDocumentsLimit",
    "burstPartialUpdatesLimit"
];
var unaddableSources = [
    SourceType.CUSTOM
];
var getLicense = function(state) {
    return state.license;
};
var getLicenseProperties = createSelector(getLicense, function(license) {
    return license === null || license === void 0 ? void 0 : license.properties;
});
var ref;
var getConnectors = createSelector(getLicense, function(license) {
    return (ref = license === null || license === void 0 ? void 0 : license.connectors) !== null && ref !== void 0 ? ref : [];
});
var getAvailableConnectors = function() {
    var isSalesforceSitesAvailable = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : false;
    return createSelector(getLicense, function(license) {
        var ref;
        var sourceTypeNotAvailable = isSalesforceSitesAvailable ? unaddableSources : _to_consumable_array(unaddableSources).concat([
            SourceType.SALESFORCE_SITES
        ]);
        var ref1;
        return (ref1 = license === null || license === void 0 ? void 0 : (ref = license.connectors) === null || ref === void 0 ? void 0 : ref.filter(function(connector) {
            return !sourceTypeNotAvailable.includes(connector.type) && Object.values(SourceType).includes(connector.type);
        })) !== null && ref1 !== void 0 ? ref1 : [];
    });
};
var getSearchProperties = createSelector(getLicenseProperties, function(licenseProperties) {
    return licenseProperties === null || licenseProperties === void 0 ? void 0 : licenseProperties.searchapi;
});
var getContentProperties = createSelector(getLicenseProperties, function(licenseProperties) {
    return licenseProperties === null || licenseProperties === void 0 ? void 0 : licenseProperties.content;
});
var getOrganizationProperties = createSelector(getLicenseProperties, function(licenseProperties) {
    return licenseProperties === null || licenseProperties === void 0 ? void 0 : licenseProperties.organization;
});
var getUsageAnalyticsProperties = createSelector(getLicenseProperties, function(licenseProperties) {
    return licenseProperties === null || licenseProperties === void 0 ? void 0 : licenseProperties.usageAnalytics;
});
var getMachineLearningProperties = createSelector(getLicenseProperties, function(licenseProperties) {
    return licenseProperties === null || licenseProperties === void 0 ? void 0 : licenseProperties.machineLearning;
});
var getCommerceProperties = createSelector(getLicenseProperties, function(licenseProperties) {
    return licenseProperties === null || licenseProperties === void 0 ? void 0 : licenseProperties.commerce;
});
var getInternalProperties = createSelector(getLicenseProperties, function(licenseProperties) {
    return licenseProperties === null || licenseProperties === void 0 ? void 0 : licenseProperties.internal;
});
var getInProductExperienceProperties = createSelector(getLicenseProperties, function(licenseProperties) {
    return licenseProperties === null || licenseProperties === void 0 ? void 0 : licenseProperties.inProductExperience;
});
var getLicenseType = createSelector(getLicense, function(license) {
    return license === null || license === void 0 ? void 0 : license.type;
});
var getProductType = createSelector(getLicense, function(license) {
    return license === null || license === void 0 ? void 0 : license.productType;
});
var getProductName = createSelector(getLicense, function(license) {
    return license === null || license === void 0 ? void 0 : license.productName;
});
var getIndexType = createSelector(getLicense, function(license) {
    return license === null || license === void 0 ? void 0 : license.indexType;
});
var getExpirationDate = createSelector(getLicense, function(license) {
    return license === null || license === void 0 ? void 0 : license.expirationDate;
});
var getProductEdition = createSelector(getLicense, function(license) {
    return license === null || license === void 0 ? void 0 : license.productEdition;
});
var getEntitlements = createSelector(getLicense, function(license) {
    return license === null || license === void 0 ? void 0 : license.entitlements;
});
var getModelConfigurability = createSelector(getLicenseProperties, function(licenseProperties) {
    var ref;
    return licenseProperties === null || licenseProperties === void 0 ? void 0 : (ref = licenseProperties.machineLearning) === null || ref === void 0 ? void 0 : ref.modelConfigurability;
});
var ref1;
var isABTestEnabled = createSelector(getSearchProperties, function(searchApiProperties) {
    return (ref1 = searchApiProperties === null || searchApiProperties === void 0 ? void 0 : searchApiProperties.abTestEnabled) !== null && ref1 !== void 0 ? ref1 : true;
});
var ref2;
var isResourceSnapshotsEnabled = createSelector(getOrganizationProperties, function(organizationProperties) {
    return (ref2 = organizationProperties === null || organizationProperties === void 0 ? void 0 : organizationProperties.snapshotsEnabled) !== null && ref2 !== void 0 ? ref2 : false;
});
var ref3;
var isModelTestingAvailable = createSelector(getMachineLearningProperties, function(machineLearningProperties) {
    return (ref3 = machineLearningProperties === null || machineLearningProperties === void 0 ? void 0 : machineLearningProperties.modelTesterEnabled) !== null && ref3 !== void 0 ? ref3 : true;
});
var isIntelligentTermDetectionEnabled = function(state) {
    var ref;
    return !!((ref = LicenseSelectors.getMachineLearningProperties(state)) === null || ref === void 0 ? void 0 : ref[LicenseConstants.ITDEnable]);
};
var isIpxEnabled = function(state) {
    var ref;
    return !!((ref = LicenseSelectors.getInProductExperienceProperties(state)) === null || ref === void 0 ? void 0 : ref[LicenseConstants.isIpxEnabled]);
};
var isSitecoreLicense = createSelector(getLicenseType, function(licenseType) {
    return licenseType ? LicenseConstants.SITECORE_LICENSES.includes(licenseType) : false;
});
var isTrialLicense = createSelector(getLicense, function(license) {
    return license ? LicenseHelper.isTrial(license) : false;
});
var isProductionOrganization = createSelector(getProductType, function(productType) {
    return productType === ProductType.STANDARD;
});
var ref4;
var isSearchGroupsEnabled = createSelector(getSearchProperties, function(properties) {
    return (ref4 = properties === null || properties === void 0 ? void 0 : properties.groupsEnabled) !== null && ref4 !== void 0 ? ref4 : true;
});
var isTrialExpired = createSelector(getLicense, function(license) {
    return license ? LicenseHelper.isTrialExpired(license) : false;
});
var hasExpired = function() {
    var state = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : LicenseStore.getState();
    return isTrialExpired(state);
};
var isTrialOrg = function() {
    var state = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : LicenseStore.getState();
    return isTrialLicense(state);
};
export var LicenseSelectors = {
    isTrialExpired: isTrialExpired,
    hasExpired: hasExpired,
    getLicense: getLicense,
    getConnectors: getConnectors,
    getAvailableConnectors: getAvailableConnectors,
    getSearchProperties: getSearchProperties,
    getContentProperties: getContentProperties,
    getCommerceProperties: getCommerceProperties,
    getInternalProperties: getInternalProperties,
    getInProductExperienceProperties: getInProductExperienceProperties,
    getOrganizationProperties: getOrganizationProperties,
    getUsageAnalyticsProperties: getUsageAnalyticsProperties,
    getMachineLearningProperties: getMachineLearningProperties,
    getLicenseType: getLicenseType,
    getProductType: getProductType,
    getProductName: getProductName,
    getIndexType: getIndexType,
    getExpirationDate: getExpirationDate,
    getProductEdition: getProductEdition,
    getEntitlements: getEntitlements,
    getModelConfigurability: getModelConfigurability,
    isABTestEnabled: isABTestEnabled,
    isProductionOrganization: isProductionOrganization,
    isResourceSnapshotsEnabled: isResourceSnapshotsEnabled,
    isSitecoreLicense: isSitecoreLicense,
    isTrialOrg: isTrialOrg,
    isTrialLicense: isTrialLicense,
    isModelTestingAvailable: isModelTestingAvailable,
    isIntelligentTermDetectionEnabled: isIntelligentTermDetectionEnabled,
    isIpxEnabled: isIpxEnabled,
    isSearchGroupsEnabled: isSearchGroupsEnabled
};

//# sourceMappingURL=LicenseSelectors.js.map